import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  deleteUser,
  confirmPasswordReset,
  type Auth,
  type User,
  type UserCredential,
  type IdTokenResult
} from "firebase/auth";
import { type AppConfig } from "@/config";

class FirebaseAuthService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private config: AppConfig | null = null;

  initialize(config: AppConfig): void {
    if (this.app && this.auth) {
      return;
    }

    try {
      this.config = config;

      const firebaseConfig = {
        apiKey: this.config.firebase.apiKey,
        authDomain: this.config.firebase.authDomain,
        projectId: this.config.firebase.projectId
      };

      this.app = initializeApp(firebaseConfig);
      this.auth = getAuth(this.app);

      console.log("Firebase initialized successfully");
    } catch (error) {
      console.error("Firebase initialization failed:", error);
      throw error;
    }
  }

  getAuth(): Auth {
    if (!this.auth) {
      throw new Error("Firebase not initialized. Call initialize() first.");
    }

    return this.auth;
  }

  getCurrentUser(): User | null {
    return this.getAuth().currentUser;
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(this.getAuth(), callback);
  }

  onIdTokenChanged(callback: (user: User | null) => void): () => void {
    return onIdTokenChanged(this.getAuth(), callback);
  }

  async signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    try {
      const result = await signInWithEmailAndPassword(this.getAuth(), email, password);
      console.log("Email/password sign-in successful");

      return result;
    } catch (error) {
      console.error("Email/password sign-in failed:", error);
      throw error;
    }
  }

  async createUserWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    try {
      const result = await createUserWithEmailAndPassword(this.getAuth(), email, password);
      console.log("User account created successfully");

      return result;
    } catch (error) {
      console.error("User account creation failed:", error);
      throw error;
    }
  }

  async signInWithGoogle(): Promise<UserCredential> {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account"
      });

      const result = await signInWithPopup(this.getAuth(), provider);
      console.log("Google sign-in successful");

      return result;
    } catch (error) {
      console.error("Google sign-in failed:", error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.getAuth());
      console.log("User signed out successfully");
    } catch (error) {
      console.error("Sign out failed:", error);
      throw error;
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.getAuth(), email);
      console.log("Password reset email sent");
    } catch (error) {
      console.error("Password reset email failed:", error);
      throw error;
    }
  }

  async sendEmailVerification(): Promise<void> {
    const user = this.getCurrentUser();

    if (!user) {
      throw new Error("No user is currently signed in");
    }

    try {
      await sendEmailVerification(user, { url: window.location.origin });
      console.log("Email verification sent");
    } catch (error) {
      console.error("Email verification failed:", error);
      throw error;
    }
  }

  async getIdToken(forceRefresh = false): Promise<string> {
    const user = this.getCurrentUser();

    if (!user) {
      throw new Error("No user is currently signed in");
    }

    try {
      const token = await user.getIdToken(forceRefresh);

      return token;
    } catch (error) {
      console.error("Failed to get ID token:", error);
      throw error;
    }
  }

  async getIdTokenResult(forceRefresh = false): Promise<IdTokenResult> {
    const user = this.getCurrentUser();

    if (!user) {
      throw new Error("No user is currently signed in");
    }

    try {
      const tokenResult = await user.getIdTokenResult(forceRefresh);

      return tokenResult;
    } catch (error) {
      console.error("Failed to get ID token result:", error);
      throw error;
    }
  }

  async isTokenExpiringSoon(bufferMinutes = 5): Promise<boolean> {
    try {
      const tokenResult = await this.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const bufferMs = bufferMinutes * 60 * 1000;

      return expirationTime - now < bufferMs;
    } catch (error) {
      console.error("Failed to check token expiration:", error);

      return true; // Assume expired if we can't check
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const user = this.getCurrentUser();

    if (!user) {
      throw new Error("No user is currently signed in");
    }

    try {
      await updatePassword(user, newPassword);
      console.log("Password updated successfully");
    } catch (error) {
      console.error("Password update failed:", error);
      throw error;
    }
  }

  async deleteUser(): Promise<void> {
    const user = this.getCurrentUser();

    if (!user) {
      throw new Error("No user is currently signed in");
    }

    try {
      await deleteUser(user);
      console.log("User deleted successfully");
    } catch (error) {
      console.error("User deletion failed:", error);
      throw error;
    }
  }

  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.getAuth(), oobCode, newPassword);
      console.log("Password reset confirmed successfully");
    } catch (error) {
      console.error("Password reset confirmation failed:", error);
      throw error;
    }
  }

  getApiBaseUrl(): string {
    if (!this.config) {
      throw new Error("Firebase not initialized. Cannot get API base URL.");
    }

    return this.config.api.baseUrl;
  }
}

// Firebase auth instance will be created in App.tsx after config is loaded
export let firebaseAuth: FirebaseAuthService;

// Initialize Firebase auth with config
export const initializeFirebaseAuth = (config: AppConfig) => {
  firebaseAuth = new FirebaseAuthService();
  firebaseAuth.initialize(config);

  return firebaseAuth;
};
